import React, { useMemo, useState } from 'react'
import { Button, FormControl, Table } from 'react-bootstrap'

import useTranslation from '@/Hooks/UseTranslation'

import Icon from '@/Components/Icon/Icon'
import Modal from '@/Components/Modal/Modal'

import './TaskImportSourceViewer.scss'

type ImportSource = {
  provider: string
  format: string
  fileName: string
  importedAt: string
  raw: Record<string, unknown>
}

type Props = {
  source?: ImportSource
  taskName: string
  recordLabelKey?: string
  relatedTask?: {
    id: string
    name: string
    onSelect?: () => void
  }
  compact?: boolean
}

type SourceField = {
  path: string
  value: string
}

type ImportUsage = {
  status: 'used' | 'partial'
  translationKey: string
}

const importUsageByPath: Record<string, ImportUsage> = {
  UID: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:UID' },
  ID: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:ID' },
  Name: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:Name' },
  Start: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:Start' },
  Finish: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:Finish' },
  Summary: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:Summary' },
  OutlineNumber: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:OutlineNumber' },
  OutlineLevel: { status: 'partial', translationKey: 'TASK_IMPORT_SOURCE:Usage:OutlineLevel' },
  PercentComplete: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:PercentComplete' },
  Milestone: { status: 'partial', translationKey: 'TASK_IMPORT_SOURCE:Usage:Milestone' },
  ConstraintType: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:ConstraintType' },
  ConstraintDate: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:ConstraintDate' },
  'PredecessorLink[].PredecessorUID': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:PredecessorUID' },
  'PredecessorLink[].Type': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:PredecessorType' },
  'PredecessorLink[].LinkLag': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:LinkLag' },
  PredecessorUID: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:PredecessorUID' },
  Type: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:PredecessorType' },
  LinkLag: { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:LinkLag' },
  'Assignment.TaskUID': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:AssignmentTaskUID' },
  'Assignment.ResourceUID': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:AssignmentResourceUID' },
  'Assignment.Work': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:AssignmentWork' },
  'Assignment.Units': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:AssignmentUnits' },
  'Assignment.Cost': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:AssignmentCost' },
  'Resource.UID': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:ResourceUID' },
  'Resource.Name': { status: 'used', translationKey: 'TASK_IMPORT_SOURCE:Usage:ResourceName' },
  'Resource.StandardRate': { status: 'partial', translationKey: 'TASK_IMPORT_SOURCE:Usage:ResourceStandardRate' },
}

const getImportUsage = (path: string): ImportUsage | undefined => {
  const normalizedPath = path.replace(/\[\d+\]/g, '[]')

  return importUsageByPath[normalizedPath]
}

const flattenSourceFields = (value: unknown, path = '', fields: SourceField[] = []): SourceField[] => {

  if (Array.isArray(value)) {

    if (!value.length) fields.push({ path, value: '[]' })

    value.forEach((item, index) => flattenSourceFields(item, `${path}[${index}]`, fields))
    return fields
  }

  if (value !== null && typeof value === 'object') {

    const entries = Object.entries(value as Record<string, unknown>)

    if (!entries.length) fields.push({ path, value: '{}' })

    entries.forEach(([ key, item ]) => {
      flattenSourceFields(item, path ? `${path}.${key}` : key, fields)
    })

    return fields
  }

  fields.push({
    path,
    value: value === null ? 'null' : String(value),
  })

  return fields
}

const TaskImportSourceViewer = ({ source, taskName, recordLabelKey = 'TASK_IMPORT_SOURCE:Task', relatedTask, compact = false }: Props) => {

  const { t } = useTranslation()
  const [ show, setShow ] = useState(false)
  const [ query, setQuery ] = useState('')

  const fields = useMemo(() => flattenSourceFields(source?.raw || {}), [ source ])
  const visibleFields = useMemo(() => {

    const normalizedQuery = query.trim().toLowerCase()

    if (!normalizedQuery) return fields

    return fields.filter((field) => (
      field.path.toLowerCase().includes(normalizedQuery)
      || field.value.toLowerCase().includes(normalizedQuery)
    ))
  }, [ fields, query ])

  if (!source) return null

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setQuery('')
  }

  const handleRelatedTaskSelect = () => {
    handleClose()
    relatedTask?.onSelect?.()
  }

  return (
    <>
      <Button
        type="button"
        variant="link"
        className={`task-import-source-button ${compact ? 'compact' : ''}`}
        aria-label={t('TASK_IMPORT_SOURCE:Open')}
        title={t('TASK_IMPORT_SOURCE:Open')}
        onClick={handleOpen}
      >
        <Icon name="database" fas />
      </Button>

      <Modal
        show={show}
        title={t('TASK_IMPORT_SOURCE:Title')}
        onClose={handleClose}
        showCloseButton
        size="lg"
        centered
        scrollable
        zIndex="2"
        className="task-import-source-modal"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        footer={(
          <Button variant="secondary" onClick={handleClose}>
            {t('TASK_IMPORT_SOURCE:Close')}
          </Button>
        )}
      >
        <div className="task-import-source-summary">
          <div><strong>{t(recordLabelKey)}:</strong> {taskName || '-'}</div>
          {relatedTask && (
            <div>
              <strong>{t('TASK_IMPORT_SOURCE:Task')}:</strong>{' '}
              {relatedTask.onSelect ? (
                <Button
                  type="button"
                  variant="link"
                  className="task-import-source-related-task"
                  onClick={handleRelatedTaskSelect}
                >
                  {relatedTask.id} — {relatedTask.name}
                </Button>
              ) : `${relatedTask.id} — ${relatedTask.name}`}
            </div>
          )}
          <div><strong>{t('TASK_IMPORT_SOURCE:Provider')}:</strong> {source.provider}</div>
          <div><strong>{t('TASK_IMPORT_SOURCE:File')}:</strong> {source.fileName}</div>
          <div><strong>{t('TASK_IMPORT_SOURCE:ImportedAt')}:</strong> {source.importedAt}</div>
        </div>

        <FormControl
          className="task-import-source-search"
          value={query}
          placeholder={t('TASK_IMPORT_SOURCE:SearchPlaceholder')}
          aria-label={t('TASK_IMPORT_SOURCE:SearchPlaceholder')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
        />

        <Table bordered hover size="sm" responsive className="task-import-source-table">
          <thead>
            <tr>
              <th>{t('TASK_IMPORT_SOURCE:Field')}</th>
              <th>{t('TASK_IMPORT_SOURCE:Value')}</th>
              <th>{t('TASK_IMPORT_SOURCE:Usage')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleFields.map((field) => {
              const usage = getImportUsage(field.path)

              return (
                <tr key={field.path}>
                  <td><code>{field.path}</code></td>
                  <td>{field.value}</td>
                  <td>
                    {usage ? (
                      <span className={`task-import-source-usage ${usage.status}`}>
                        <Icon name={usage.status === 'used' ? 'check' : 'info-circle'} fas fw />
                        <span>{t(usage.translationKey)}</span>
                      </span>
                    ) : (
                      <span className="task-import-source-usage unused">
                        <Icon name="minus" fas fw />
                        <span>{t('TASK_IMPORT_SOURCE:NotUsed')}</span>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>

        {!visibleFields.length && (
          <div className="text-muted py-3 text-center">
            {t('TASK_IMPORT_SOURCE:NoResults')}
          </div>
        )}
      </Modal>
    </>
  )
}

export default TaskImportSourceViewer
